import type { Coord } from '@sea-wars/game-core';
import { hashSeed } from '@sea-wars/ui';
import { useLayoutEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useChallenges } from '../api/challenges.js';
import { useSession } from '../api/session.js';
import { GameBoards } from '../components/GameBoards.js';
import { PlacementEditor } from '../components/PlacementEditor.js';
import { useGame } from '../ws/useGame.js';

const STATUS_LABEL: Record<string, string> = {
  connecting: 'łączenie…',
  open: 'połączono',
  reconnecting: 'wznawianie połączenia…',
  closed: 'rozłączono',
};

export function GamePage() {
  const params = useParams();
  const navigate = useNavigate();
  const { user } = useSession();
  const { incoming, outgoing, requestRematch } = useChallenges();
  const [requestingRematch, setRequestingRematch] = useState(false);

  const { view, connection, error, send, placeFleet, redirectToLobby } = useGame(params.gameId);

  // Stary/martwy link (F5 albo restart PWA na już zakończonej partii, albo partia,
  // która w ogóle nie jest już nasza) — `/game/:id` to widok LIVE, nie przegląd
  // historii, więc lobby jest właściwym punktem startowym. Patrz `useGame`.
  // `useLayoutEffect`, nie `useEffect`: przenosi nawigację przed odmalowaniem klatki,
  // żeby stary ekran partii nie mignął choćby na moment.
  useLayoutEffect(() => {
    if (redirectToLobby) navigate('/', { replace: true });
  }, [redirectToLobby, navigate]);

  const opponent = view.players.find((player) => player.seat !== view.you);
  // Rewanż idzie REST-em przez `ChallengesProvider` (globalny popup, patrz `08-decyzje.md`
  // #21), nie WS-em — `outgoing`/`incoming` żyją tam, niezależnie od tego, czy ten ekran jest
  // w ogóle zamontowany. Nawigacja po zaakceptowanej ofercie dzieje się centralnie tam, nie tu.
  const iWantRematch =
    requestingRematch ||
    outgoing.some(
      (item) =>
        item.kind === 'rematch' && item.sourceGameId === view.gameId && item.status === 'pending',
    );
  const opponentWantsRematch = incoming.some(
    (item) => item.kind === 'rematch' && item.sourceGameId === view.gameId,
  );

  async function requestRematchClick(): Promise<void> {
    if (!view.gameId || !opponent) return;
    setRequestingRematch(true);
    const result = await requestRematch(view.gameId, opponent.nickname).catch(
      () => 'error' as const,
    );
    // 'offered'/'ready' zostają "zajęte" — `outgoing` w końcu to potwierdzi (do 4 s), nawigacja
    // przy 'ready' dzieje się już w `ChallengesProvider`. Tylko `busy`/błąd odblokowują przycisk.
    if (result !== 'offered' && result !== 'ready') setRequestingRematch(false);
  }

  return (
    <>
      <div className="card game__header">
        <div>
          <h2 className="hand">
            {opponent
              ? `${user?.nickname} kontra ${opponent.nickname}`
              : 'Oczekiwanie na przeciwnika'}
          </h2>
          <p className="app__note">
            <span className={`conn conn--${connection}`}>
              {STATUS_LABEL[connection] ?? connection}
            </span>
            {opponent && !opponent.online && ' · przeciwnik stracił połączenie'}
            {opponentWantsRematch && !iWantRematch && ' · przeciwnik chce rewanżu'}
          </p>
        </div>
        <Link to="/" className="btn btn--ghost">
          Lobby
        </Link>
      </div>

      <p className="app__status hand" role="status" aria-live="polite">
        {view.message}
      </p>
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {/*
       * `view.gameId` dopiero po prawdziwym snapshocie z serwera (`EMPTY_VIEW.gameId` jest
       * `null`) — bez tego warunku plansza rozstawiania renderowałaby się od razu przy
       * montowaniu, jeszcze zanim WS zdąży odpowiedzieć, więc gracz widziałby na moment
       * planszę ZANIM byłoby wiadomo, czy to w ogóle żywa/aktualna partia (a nie martwy
       * link do dawno zakończonej gry — patrz `redirectToLobby` w `useGame`).
       */}
      {view.status === 'placement' &&
        view.gameId &&
        (view.ownReady ? (
          <div className="card">
            <p className="hand">Flota zatwierdzona. Czekamy na przeciwnika…</p>
          </div>
        ) : (
          <PlacementEditor
            disabled={connection !== 'open'}
            gameId={view.gameId}
            onConfirm={placeFleet}
          />
        ))}

      {/* Rezygnacja przed pierwszym strzałem — bez porażki i bez czekania 2 minut na walkower
          (dawniej jedyne wyjście: zamknąć kartę). Po starcie strzelania zostaje „Poddaj się”. */}
      {view.status === 'placement' && view.gameId && (
        <div className="app__controls">
          <button
            type="button"
            className="btn btn--ghost"
            disabled={connection !== 'open'}
            onClick={() => {
              if (window.confirm('Anulować partię? Nie będzie liczona do statystyk.'))
                send({ type: 'game.cancel' });
            }}
          >
            Anuluj partię
          </button>
        </div>
      )}

      {view.status === 'finished' && view.endReason === 'cancelled' && (
        <div className="card">
          <p className="hand">Partia anulowana — nie liczy się do statystyk.</p>
          <div className="app__controls">
            <Link to="/" className="btn btn--primary">
              Wróć do lobby
            </Link>
          </div>
        </div>
      )}

      {view.status !== 'placement' && view.endReason !== 'cancelled' && (
        <>
          <GameBoards
            view={view}
            error={error}
            seed={hashSeed(view.gameId ?? 'sea-wars')}
            onFire={(cell: Coord) => send({ type: 'game.fire', x: cell.x, y: cell.y })}
          />
          <div className="app__controls">
            {view.status === 'in_progress' && (
              <button
                type="button"
                className="btn"
                onClick={() => {
                  if (window.confirm('Na pewno poddajesz tę partię?'))
                    send({ type: 'game.resign' });
                }}
              >
                Poddaj się
              </button>
            )}
            {view.status === 'finished' && (
              <>
                <button
                  type="button"
                  className="btn btn--primary"
                  onClick={() => void requestRematchClick()}
                  disabled={iWantRematch}
                >
                  {iWantRematch ? 'Czekasz na przeciwnika…' : 'Zagraj rewanż'}
                </button>
                <Link to="/" className="btn btn--ghost">
                  Wróć do lobby
                </Link>
              </>
            )}
          </div>
        </>
      )}
    </>
  );
}
